/**
 * PagosPresupuestoPanel
 * ----------------------
 * Lista los pagos confirmados de un presupuesto y permite al admin/comercial
 * iniciar una devolución parcial o total (Redsys refund) sobre cada uno.
 *
 * Sólo se muestran los pagos en estado `pagado` con `pasarela === "redsys"`.
 * Pagos manuales o MOCK aparecen marcados pero sin botón de devolución
 * (la devolución la tiene que hacer el banco en esos casos).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { RotateCcw, CheckCircle2, AlertTriangle, Loader2, FileDown } from "lucide-react";

const fmtEur = (v) => `${Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtFecha = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso.slice(0, 16); }
};

export default function PagosPresupuestoPanel({ presupuestoId, onChange }) {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagoSel, setPagoSel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pagos/cliente/${presupuestoId}`);
      setPagos(data.pagos || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (presupuestoId) load(); /* eslint-disable-next-line */ }, [presupuestoId]);

  if (loading) {
    return (
      <div className="text-xs text-zinc-500 py-2 flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando pagos…
      </div>
    );
  }

  if (pagos.length === 0) return null;

  return (
    <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 mb-5" data-testid="panel-pagos">
      <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold mb-3 flex items-center gap-1.5">
        💳 Pagos del cliente
      </div>
      <div className="space-y-2">
        {pagos.map((p) => (
          <PagoRow key={p.id} pago={p} onDevolver={() => setPagoSel(p)} />
        ))}
      </div>
      {pagoSel && (
        <DevolverDialog
          pago={pagoSel}
          onClose={() => setPagoSel(null)}
          onDone={() => { setPagoSel(null); load(); onChange?.(); }}
        />
      )}
    </div>
  );
}

function PagoRow({ pago, onDevolver }) {
  const esRedsys = pago.pasarela === "redsys";
  const importe = Number(pago.importe || 0);
  const devuelto = Number(pago.importe_devuelto || 0);
  const restante = Math.max(importe - devuelto, 0);
  const totalmenteDevuelto = restante < 0.01 && devuelto > 0;

  const estadoColor = {
    pagado: totalmenteDevuelto
      ? "bg-zinc-800 text-zinc-400 border-zinc-700"
      : devuelto > 0
        ? "bg-orange-500/10 text-orange-300 border-orange-500/30"
        : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    iniciado:  "bg-zinc-800 text-zinc-400 border-zinc-700",
    fallido:   "bg-red-500/10 text-red-300 border-red-500/30",
  }[pago.estado] || "bg-zinc-800 text-zinc-400 border-zinc-700";

  const label = totalmenteDevuelto
    ? "DEVUELTO"
    : devuelto > 0
      ? "PARCIAL DEVUELTO"
      : (pago.estado || "—").toUpperCase();

  return (
    <div className="flex items-center gap-3 p-2.5 bg-zinc-950 border border-zinc-800 rounded-lg flex-wrap"
      data-testid={`pago-row-${pago.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
          {fmtEur(importe)}
          <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${estadoColor}`}>{label}</span>
          {!esRedsys && (
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-zinc-800 border-zinc-700 text-zinc-500"
              title="Pago manual o MOCK — no devolvible vía pasarela">
              {pago.pasarela?.toUpperCase() || "MANUAL"}
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          {pago.concepto || pago.tipo || "Pago"} · {fmtFecha(pago.updated_at || pago.created_at)}
          {pago.transaction_id && <span className="ml-1 font-mono text-zinc-600">· Ref. {pago.transaction_id.slice(0, 16)}</span>}
        </div>
        {devuelto > 0 && !totalmenteDevuelto && (
          <div className="text-[10px] text-orange-300 mt-0.5">
            Devuelto: {fmtEur(devuelto)} · disponible para devolver: {fmtEur(restante)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {pago.estado === "pagado" && pago.numero_recibo && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/pagos/${pago.id}/recibo.pdf`}
            target="_blank" rel="noreferrer"
            className="text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
            data-testid={`recibo-${pago.id}`}
            title="Descargar recibo PDF">
            <FileDown className="w-3.5 h-3.5" /> Recibo
          </a>
        )}
        {esRedsys && pago.estado === "pagado" && !totalmenteDevuelto && (
          <Button
            size="sm" variant="outline"
            onClick={onDevolver}
            data-testid={`devolver-${pago.id}`}
            className="h-7 text-xs bg-transparent border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Devolver
          </Button>
        )}
      </div>
    </div>
  );
}

function DevolverDialog({ pago, onClose, onDone }) {
  const importe = Number(pago.importe || 0);
  const devuelto = Number(pago.importe_devuelto || 0);
  const disponible = Math.max(importe - devuelto, 0);
  const [tipo, setTipo] = useState("total");        // "total" | "parcial"
  const [importeParcial, setImporteParcial] = useState(disponible.toFixed(2));
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  const importeAEnviar = tipo === "total" ? null : Number(importeParcial);

  const submit = async () => {
    if (tipo === "parcial") {
      const v = Number(importeParcial);
      if (!v || v <= 0) { toast.error("Importe parcial inválido"); return; }
      if (v > disponible + 0.01) { toast.error(`Máximo devolvible: ${fmtEur(disponible)}`); return; }
    }
    if (!motivo.trim()) {
      if (!window.confirm("¿Seguro que quieres emitir la devolución sin motivo? Queda registrada en auditoría.")) return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/pagos/${pago.id}/devolver`, {
        importe: importeAEnviar,
        motivo: motivo.trim() || null,
      });
      toast.success(`Devolución autorizada · ${fmtEur(data.importe_devuelto)} (${data.estado_devolucion})`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error emitiendo devolución", { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="devolucion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-orange-400" /> Devolución VISA · TPV La Caixa
          </DialogTitle>
        </DialogHeader>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 text-xs space-y-1">
          <div className="flex justify-between"><span className="text-zinc-400">Pago original</span><b>{fmtEur(importe)}</b></div>
          <div className="flex justify-between"><span className="text-zinc-400">Ya devuelto</span><b className="text-orange-300">{fmtEur(devuelto)}</b></div>
          <div className="flex justify-between text-sm pt-1 mt-1 border-t border-zinc-800">
            <span className="text-zinc-300">Disponible para devolver</span>
            <b className="text-emerald-300">{fmtEur(disponible)}</b>
          </div>
        </div>

        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTipo("total")}
              data-testid="devolucion-tipo-total"
              className={`p-2.5 rounded border text-sm transition-colors ${
                tipo === "total"
                  ? "bg-orange-500/20 border-orange-500/40 text-orange-200 font-semibold"
                  : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              Devolver todo<br /><span className="text-[10px] opacity-70">{fmtEur(disponible)}</span>
            </button>
            <button
              type="button"
              onClick={() => setTipo("parcial")}
              data-testid="devolucion-tipo-parcial"
              className={`p-2.5 rounded border text-sm transition-colors ${
                tipo === "parcial"
                  ? "bg-orange-500/20 border-orange-500/40 text-orange-200 font-semibold"
                  : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              Parcial
            </button>
          </div>

          {tipo === "parcial" && (
            <div>
              <Label className="text-xs">Importe a devolver (€)</Label>
              <Input
                type="number" step="0.01" min="0.01" max={disponible}
                value={importeParcial}
                onChange={(e) => setImporteParcial(e.target.value)}
                data-testid="devolucion-importe-input"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Motivo (queda en auditoría)</Label>
            <Textarea
              rows={2}
              placeholder="Ej: producto defectuoso, cancelación de pedido…"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              data-testid="devolucion-motivo"
              className="bg-zinc-950 border-zinc-800"
            />
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2.5 text-[11px] text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              La devolución se ejecuta directamente sobre la <b>tarjeta original</b> del cliente vía Redsys. Si el cobro tiene más de 48h o ya fue conciliado, podrías tener que tramitarlo manualmente desde Comercia Global Payments.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}
            data-testid="devolucion-cancelar"
            className="text-zinc-400">
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="devolucion-confirmar"
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando…</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar devolución</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
